import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

import { sensorStatusColors } from '../configs/sensorStatusColors';
import { sensorStatusLabels } from '../configs/sensorStatusLables';
import { SensorStatus } from '../types/SensorStatus';

export interface SensorStatusChipProps {
  status: SensorStatus | string;
  maxWidth?: number;
  fontSize?: number;
  isPresentEmphasis?: boolean;
}

const StyledChip = styled(Chip, {
  // Keep custom styling props off the underlying DOM node (avoids React
  // "unknown prop" warnings in the console).
  shouldForwardProp: (prop) =>
    !['status', 'maxWidth', 'fontSize', 'isPresentEmphasis'].includes(
      prop as string,
    ),
})(
  ({
    status,
    maxWidth,
    fontSize,
    isPresentEmphasis,
  }: SensorStatusChipProps) => {
    const commons = {
      borderRadius: '11.5px',
      fontSize: fontSize || 14,
      fontWeight: 'bold',
      lineHeight: 1,
      letterSpacing: -0.01,
      height: fontSize ? Math.max(23, fontSize + 9) : 23,
      '> span': {
        padding: '2px 14px 0px',
      },
      maxWidth: maxWidth || 'unset',
    };
    if (isPresentEmphasis) {
      // "present" badge sits on the colored present card → a white pill that
      // pops against it.
      return {
        ...commons,
        border: `solid 1px ${sensorStatusColors.IN}`,
        backgroundColor: '#fff',
        color: sensorStatusColors.IN,
      };
    }
    if (sensorStatusColors[status as SensorStatus] === undefined) {
      // unknown / custom status → neutral outlined pill
      return {
        ...commons,
        border: 'solid 1px #c7ccd1',
        backgroundColor: '#fff',
        color: '#5f6b7a',
      };
    }
    // absent (gray) and meeting (amber) → filled with white text for legibility
    return {
      ...commons,
      backgroundColor: sensorStatusColors[status as SensorStatus],
      color: '#fff',
    };
  },
);

export function SensorStatusChip({ status, ...props }: SensorStatusChipProps) {
  return (
    <StyledChip
      status={status}
      label={sensorStatusLabels[status as SensorStatus] || status}
      {...props}
    />
  );
}
